import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../../api/store";
import { v4 as uuidv4 } from "uuid";
import { SINGLE_RIDER_IDS_COVERAGE, SINGLE_RIDER_IDS_DISEASE, SINGLE_RIDER_IDS_GCI_DENGUE_MOSQUITO, SINGLE_RIDER_IDS_GCI_TOP4, SINGLE_RIDER_IDS_GCIR } from "../../../../constant/gms-constant";
import { AddRiderLevelRankingInputPayload, initialInputs, InputByLevelRanking, LevelRankingState } from "./coverage-type-level-ranking.state";

const initialState: LevelRankingState  = {
    inputs: initialInputs,
    riders: [],
};

const levelRankingSlice = createSlice({
    name: "levelRankingSlice",
    initialState,
    reducers: {
        resetLevelRanking: () => ({
            inputs: [
                { id: uuidv4(), label: "", fAmount: 0 },
                { id: uuidv4(), label: "", fAmount: 0 },
            ],
            riders: [],
        }),
        // Add a new row (max 10)
        addRowInputLevelRanking: (state) => {
            const hasEmptyRow = state.inputs.some(input => input.label === "" || input.fAmount === 0);
            
            if(hasEmptyRow) {
                return;
            }

            const newRow: InputByLevelRanking = {
                fAmount: 0,
                id: uuidv4(),
                label: ""
            }

            state.inputs.push(newRow);

            state.riders?.forEach(rider => {
                rider.inputs.push({ ...newRow})
            })

            // if (state.inputs.length < 10) {
            //     state.inputs.push(newRow);
            // }
        },
        updateInputLevelRanking: (state, action: PayloadAction<{ id: string; label?: string; fAmount?: number }>) => {
            const { id, label, fAmount } = action.payload;

            const input = state.inputs.find(i => i.id === id);
            if (!input) return;

            if (label !== undefined) input.label = label;
            if (fAmount !== undefined) input.fAmount = fAmount;

            state?.riders?.forEach(rider => {
                const riderInput = rider.inputs.find(i => i.id === id);
                if (!riderInput) return;

                if (label !== undefined) riderInput.label = label;
                if (fAmount !== undefined) riderInput.fAmount = fAmount;
            });
        },
        addRiderLevelRankingInput: (state, action: PayloadAction<AddRiderLevelRankingInputPayload>) => {
            const { id, label, inputs, values = [], riderValues = []} = action.payload;
            const riderId = Number(id);

            const singleSelectGCIR: number[] = SINGLE_RIDER_IDS_GCIR;
            const singleSelectGroup: number[] = SINGLE_RIDER_IDS_COVERAGE;
            const singleSelectDisease: number[] = SINGLE_RIDER_IDS_DISEASE; // 13,16,19

            const singleSelectGCI_TOP4: number[] = SINGLE_RIDER_IDS_GCI_TOP4;
            const singleSelectGCI_DENGUE_MOSQUITO: number[] = SINGLE_RIDER_IDS_GCI_DENGUE_MOSQUITO;

            // If rider belongs to single select group
            if (singleSelectGroup.includes(id)) {
                state.riders = state.riders?.filter(
                    r => !singleSelectGroup.includes(r.id)
                ) || [];
            }

            if (singleSelectGCIR.includes(id)) {
                state.riders = state.riders?.filter(
                    r => !singleSelectGCIR.includes(r.id)
                ) || [];
            }

            if (singleSelectDisease.includes(id)) {
                state.riders = state.riders?.filter(
                    r => !singleSelectDisease.includes(r.id)
                ) || [];
            }
            // GCI
            if (singleSelectGCI_TOP4.includes(id)) {
                state.riders = state.riders?.filter(
                    r => !singleSelectGCI_TOP4.includes(r.id)
                ) || [];
            }
            if (singleSelectGCI_DENGUE_MOSQUITO.includes(id)) {
                state.riders = state.riders?.filter(
                    r => !singleSelectGCI_DENGUE_MOSQUITO.includes(r.id)
                ) || [];
            }

            const exists = state.riders?.some(r => r.id === id);
            if (exists) return;

            let finalRows: InputByLevelRanking[] = [];

            if(inputs && inputs.length > 0) {
                finalRows = inputs.map(v => {
                    const baseRow = state.inputs.find(r => r.label === v.label);

                    return {
                        id: baseRow?.id ?? uuidv4(),
                        fAmount: v.fAmount,
                        label: baseRow?.label ?? v.label,
                    }
                });
            } else {
                finalRows = state.inputs.map(row => ({
                    ...row,
                    id: row.id ?? uuidv4(),
                }));
            }

            state.riders?.push({
                id,
                label,
                inputs: finalRows
            });
        },
        removeLevelRankingRiderInput: (state, action: PayloadAction<number>) => {
            state.riders = state.riders?.filter(r => r.id !== action.payload);
        },
        removeLevelRankingRiderByIndex: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            
            if (state.inputs.length <= 2) return;
            state.inputs = state.inputs.filter(row => row.id !== id);

            state?.riders?.forEach(rider => {
                rider.inputs = rider?.inputs?.filter(row => row.id !== id);
            });
        },
        updateLevelRankingRiderInput: (state, action: PayloadAction<{ riderId: number; inputIndex: number; fAmount: number }>) => {
            const { riderId, inputIndex, fAmount } = action.payload;
            const rider = state.riders?.find(r => r.id === riderId);
            if (rider && rider.inputs[inputIndex]) {
                rider.inputs[inputIndex].fAmount = fAmount; // store as string
            }
        },
        setLevelRankingFromAPI: ( state, action: PayloadAction<{ id: string, label: string; fAmount: number }[]>) => {
            state.inputs = action.payload;
        },
        resetLevelRankingType: () => ({ ...initialState }),
    },
});

export const { 
    resetLevelRanking, 
    addRowInputLevelRanking, 
    updateInputLevelRanking,
    addRiderLevelRankingInput,
    removeLevelRankingRiderInput,
    updateLevelRankingRiderInput,
    setLevelRankingFromAPI,
    resetLevelRankingType,
    removeLevelRankingRiderByIndex,
} = levelRankingSlice.actions;

export const selectLevelRankingInputs = (state: RootState) => state.levelRankingSlice.inputs;
export const selectLevelRankingRiders = (state: RootState) => state.levelRankingSlice.riders;

export default levelRankingSlice.reducer;

